import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Avatar, Button, Popover, Skeleton } from 'antd';
import {
  ApartmentOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  LikeFilled,
  LikeOutlined,
  FlagOutlined,
  MessageOutlined,
  NotificationOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  TrophyOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import VerifiedAccount from '../Base/VerifiedAccount';
import { feedRequest, readReactions } from '../../lib/feedApi';
import type { FeedReaction } from '../../lib/feedInteractions';
import FeedPlayerPreview from './FeedPlayerPreview';
import useFeedPreferences from './useFeedPreferences';
import { MAX_HIDDEN_FEED_EVENTS } from '../../shared/utils/feed-preferences';
import { MATCH_NOTE_WINDOW_MS } from '../../shared/utils/league-match-note';
import { trackConversion } from '../../lib/conversionEvents';
import {
  setFeedPositionOwner,
  MAX_RESTORE_PAGES,
  saveFeedPosition,
  takeFeedPosition,
  type FeedPosition,
} from '../../shared/utils/feed-position';
import MatchPhoto from './MatchPhoto';
import FeedMatchStory from './FeedMatchStory';
import FeedWelcome from './FeedWelcome';
import FeedMatchCatchUp from './FeedMatchCatchUp';
import FeedCongratsInbox from './FeedCongratsInbox';
import FeedStoryReminder from './FeedStoryReminder';
import { returnCongratsRows } from '../../shared/utils/feed-congrats-inbox';
import LeagueShareButton from '../ListDetail/LeagueShareButton';
import MatchNotes from './MatchNotes';
import FeedPostLink from './FeedPostLink';
import FeedPostMenu from './FeedPostMenu';
import FeedPostText from './FeedPostText';
import FeedPostPhoto from './FeedPostPhoto';
import useFeedIncoming from './useFeedIncoming';
import usePullToRefresh from './usePullToRefresh';
import useScrollAnchor from './useScrollAnchor';
import useFeedSeen from './useFeedSeen';
import { newestFeedTime } from '../../shared/utils/feed-live';
import FeedPhotoStrip, { type PhotoStripItem } from './FeedPhotoStrip';
import { feedVideoEmbed } from '../../shared/utils/feed-post';
import {
  normalizeReactionCounts,
  REACTION_EMOJI,
  topReactionKinds,
  type ReactionKind,
} from '../../shared/utils/feed-reactions';
import { getDate } from '../../shared/utils/date';
import {
  encodeFeedCursor,
  feedDay,
  feedItemEvents,
  feedSubjectIds,
  formatSets,
  groupFeed,
  isFeedRival,
  isNearRank,
  mergeFeedPages,
  liftPopular,
  pinLastNight,
  rankForViewer,
  playerName,
  sentenceToken,
  splitSentence,
} from '../../shared/utils/league-feed';
import { showNotification } from '../../shared/utils/notifications';
import { ROUTE_LOGIN, ROUTE_PLAY } from '../../shared/variables/routes';
import { partnerExpired } from '../../shared/utils/feed-partner';
import FeedPartnerCard, { PartnerOffer } from './FeedPartnerCard';
import FeedAnnouncementMenu from './FeedAnnouncementMenu';
import { feedKeyMove, focusOutside } from '../../shared/utils/feed-keyboard';
import {
  clearCongratsIntent,
  pendingCongratsIntent,
  rememberCongratsIntent,
} from '../../shared/utils/feed-congrats-intent';
import { convertTitleToUrl } from '../../shared/utils/url';
import { storageImage } from '../../shared/utils/storage-image';

const LOCALES: Record<string, string> = { tr: 'tr-TR', en: 'en-GB', ru: 'ru-RU' };
const REACTABLE: LeagueFeed.Type[] = ['katildi', 'mac_sonucu', 'kilometre_tasi', 'gonderi'];
// Yeni gelen kartın giriş/vurgu süresi; sonra sınıf kaldırılır (yeniden çizimde tekrar oynamaz).
const FRESH_MS = 1800;
const REACTION_RETRY_MS = [1000, 3000, 8000];
type FeedTask = 'inbox' | 'welcome' | 'catchup' | 'story';
const STORY_REMINDER_MS = 2 * 24 * 60 * 60 * 1000;
// Sunucuda çizimde useEffect (uyarısız); tarayıcıda boyamadan önce (sıralama değişimi görünmesin).
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
type Fresh = Record<string, { kind: 'new' | 'page'; order: number }>;

// Fotoğraf şeridi: son 7 günün fotoğraflı kartları, en çok 10.
const PHOTO_STRIP_DAYS = 7;
const PHOTO_STRIP_SIZE = 10;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

type Props = {
  leagueId: string;
  initialEvents: LeagueFeed.Event[];
  initialHasMore: boolean;
  initialNextCursor?: string | null;
  // İlk sayfa sunucuda okunamadı: boş akış yerine hata ve yeniden deneme gösterilir.
  initialError?: boolean;
  viewer?: Users.User;
  members: Users.User[];
  cannotOffers: string[];
  // eslint-disable-next-line no-unused-vars -- callback signature
  onSelectPlayer?: (player: Users.User) => void;
  // Ana sayfa önizlemesi: sayfalama yerine alt bağlantı gösterilir.
  footer?: React.ReactNode;
  removedPhotoIds?: string[];
  // eslint-disable-next-line no-unused-vars -- callback signature
  onPhotoRemoved?: (id: string) => void;
  // Lig dışı akış (ana sayfa): sayfa ucu ve olay başına kaynak/lig bağlamı.
  // eslint-disable-next-line no-unused-vars -- callback signature
  fetchPage?: (cursor: string | null, signal: AbortSignal) => Promise<LeagueFeed.Page>;
  initialContext?: Record<string, LeagueFeed.HomeContext>;
  // Boş akışta lig metni yerine gösterilecek içerik (ana sayfa).
  empty?: React.ReactNode;
  // Akışın üstünde fotoğraflı kartlara kısayol şeridi (ana sayfa).
  photoStrip?: boolean;
  // Paylaşılan kart (`?olay=`, LG-67): listede varsa ona gidilir, yoksa üstte ayrıca gösterilir.
  sharedEvent?: LeagueFeed.Event | null;
  // Lig sayfasında paylaşım kutusundan gönderi çıkınca artar; akış yeniden okunur.
  // eslint-disable-next-line react/require-default-props -- yoksa yeniden okuma yok
  reloadSignal?: number;
  // Lig sayfasında ligin adı (partner kartının yer satırı; ana sayfada olay bağlamından gelir).
  // eslint-disable-next-line react/require-default-props -- yoksa yer satırı yok
  leagueName?: string;
};

export default function LeagueFeedList({
  leagueId,
  initialEvents,
  initialHasMore,
  initialNextCursor,
  initialError,
  viewer,
  members,
  cannotOffers,
  onSelectPlayer,
  footer,
  removedPhotoIds = [],
  onPhotoRemoved,
  fetchPage,
  initialContext,
  empty,
  photoStrip,
  sharedEvent,
  reloadSignal,
  leagueName,
}: Props) {
  const { t, i18n } = useTranslation('leagues');
  const locale = LOCALES[(i18n.language || 'tr').slice(0, 2)] || LOCALES.tr;
  const scope = `${leagueId}:${viewer?.id || 'anonymous'}:${viewer?.lig_temel_id || ''}`;
  const [eventScope, setEventScope] = useState(scope);
  const [events, setEvents] = useState(initialEvents);
  const [shared, setShared] = useState(sharedEvent || null);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [nextCursor, setNextCursor] = useState(initialNextCursor ?? null);
  const [firstPageError, setFirstPageError] = useState(!!initialError);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState<string[]>([]);
  const [context, setContext] = useState(initialContext);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [notice, setNotice] = useState('');
  const [reactionState, setReactionState] = useState<Record<string, FeedReaction>>({});
  const [reactionError, setReactionError] = useState(false);
  const [reactionRetry, setReactionRetry] = useState(0);
  const [preview, setPreview] = useState<{ id: string; scope: string } | null>(null);
  const [fresh, setFresh] = useState<Fresh>({});
  // "Maçını anlat" satırındaki "Not ekle" ilgili kartın not alanını açar.
  const [noteSignal, setNoteSignal] = useState<Record<string, number>>({});
  // Saat bilgisi yalnız tarayıcıda: göreli zaman ve "Dün gece" sabitlemesi ilk çizimde sunucuyla aynı.
  const [now, setNow] = useState<number | null>(null);
  // Son onaylanan tebrik: düğme ve sayı kısa bir hareketle karşılık verir; fotoğraftan geldiyse kalp.
  const [popped, setPopped] = useState<{ id: string; key: number; photo: boolean } | null>(null);
  const freshTimers = useRef<number[]>([]);
  const pullIndicator = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const preferenceFocus = useRef<HTMLDivElement>(null);
  const pages = useRef(1);
  const locks = useRef(new Set<string>());
  const reactionVersions = useRef(new Map<string, number>());
  const reactionAttempts = useRef(0);
  const resetOnce = useRef(false);
  const [reactionOwner, setReactionOwner] = useState(leagueId);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const visibleEvents = eventScope === scope ? events : initialEvents;
  const visibleContext = eventScope === scope ? context : initialContext;
  const preferences = useFeedPreferences(viewer?.id);
  // Okunan kart, üstündeki modül/fotoğraf değişince yerinden kaymaz (mobil).
  useScrollAnchor(
    root,
    '.league-feed__top, .league-feed__strip-row, [data-feed-id]',
    undefined,
    visibleEvents.length > 0,
  );
  // İzleyicinin tebrik durumu: sunucudan okunan (API) ya da kartın kendi listesinden. Liste tamsa
  // (`tebrik_edenler` sayacı karşılıyor) ya da izleyici listedeyse durum kesindir; uygulama yeniden
  // açılınca API yanıtı gecikse/başarısız olsa da düğme doğru görünür (3 Kasım hata bildirimi).
  const reactionOf = (event: LeagueFeed.Event): FeedReaction | undefined => {
    const loaded = reactionOwner === scope ? reactionState[event.id] : undefined;
    if (loaded || !viewer?.id) return loaded;
    const listed = event.tebrik_edenler.includes(viewer.id);
    if (!listed && event.tebrik_edenler.length < event.tebrik_sayisi) return undefined;
    const count = Math.max(event.tebrik_sayisi, listed ? 1 : 0);
    return {
      selected: listed,
      count,
      preview: event.tebrik_onizleme || [],
      kind: listed ? 'alkis' : null,
      counts: normalizeReactionCounts(event.tepki_sayilari, count),
    };
  };
  const reactionIds = visibleEvents
    .filter(event => REACTABLE.includes(event.tur) && !preferences.hidden.has(event.id))
    .map(event => event.id)
    .join(',');

  const capturePosition = (): FeedPosition | null => {
    const focused = document.activeElement?.closest<HTMLElement>('[data-feed-id]');
    const visible = (node: HTMLElement) =>
      node.getBoundingClientRect().bottom > 0 &&
      node.getBoundingClientRect().top < window.innerHeight;
    const card =
      focused && root.current?.contains(focused) && visible(focused)
        ? focused
        : Array.from(root.current?.querySelectorAll<HTMLElement>('[data-feed-id]') || []).find(
            visible,
          );
    if (!card) return null;
    return {
      pages: pages.current,
      eventId: card.dataset.feedId || '',
      top: card.getBoundingClientRect().top,
      focus: card.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)?.dataset.feedFocus || ''
        : '',
      at: Date.now(),
    };
  };
  const restorePosition = (position: FeedPosition | null) => {
    if (!position) return;
    requestAnimationFrame(() => {
      if (currentScope.current !== scope || !root.current) return;
      const card = Array.from(root.current.querySelectorAll<HTMLElement>('[data-feed-id]')).find(
        node =>
          node.dataset.feedId === position.eventId ||
          node.dataset.feedEvents?.split(' ').includes(position.eventId),
      );
      if (!card) {
        setNotice(t('detail.feed.positionChanged'));
        return;
      }
      const focus = Array.from(card.querySelectorAll<HTMLElement>('[data-feed-focus]')).find(
        node => node.dataset.feedFocus === position.focus,
      );
      const disclosure = focus?.closest('details');
      if (disclosure && focus?.tagName !== 'SUMMARY') disclosure.open = true;
      focus?.focus({ preventScroll: true });
      const html = document.documentElement;
      const behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollBy(0, card.getBoundingClientRect().top - position.top);
      html.style.scrollBehavior = behavior;
      trackConversion('feed_position_restored');
    });
  };
  // Lig değişince uçuştaki istek iptal edilir; geç dönen eski lig yanıtı state'e yazılmaz.
  const request = useRef<{ leagueId: string; controller: AbortController } | null>(null);

  useEffect(() => {
    request.current?.controller.abort();
    request.current = null;
    setEvents(initialEvents);
    setHasMore(initialHasMore);
    setNextCursor(initialNextCursor ?? null);
    setFirstPageError(!!initialError);
    setContext(initialContext);
    setLoadingMore(false);
    setLoadError(false);
    setRefreshing(false);
    setRefreshError(false);
    setNotice('');
    setReactionState({});
    setReactionOwner(scope);
    // Aynı kart kimlikleriyle sıfırlanırsa okuma etkisi yeniden çalışmaz; ilk kurulum hariç tetiklenir.
    reactionAttempts.current = 0;
    if (resetOnce.current) setReactionRetry(value => value + 1);
    resetOnce.current = true;
    setPending([]);
    setFresh({});
    setPopped(null);
    locks.current.clear();
    pages.current = 1;
    setEventScope(scope);
    setFeedPositionOwner(viewer?.id || 'anonymous');
    const position = takeFeedPosition(scope);
    if (!position) return undefined;
    if (position.pages <= 1 || !initialHasMore) {
      restorePosition(position);
      return undefined;
    }
    const own = { leagueId, controller: new AbortController() };
    request.current = own;
    setRefreshing(true);
    (async () => {
      try {
        let all = initialEvents;
        let allContext = initialContext;
        let cursor = initialNextCursor || null;
        let more: boolean = initialHasMore;
        let count = 1;
        while (cursor && more && count < position.pages && count < MAX_RESTORE_PAGES) {
          // eslint-disable-next-line no-await-in-loop -- devam imleci önceki yanıttan gelir
          const page = await readPage(cursor, own.controller.signal);
          all = mergeFeedPages(all, page.events);
          if ((page as LeagueFeed.HomePage).context)
            allContext = { ...allContext, ...(page as LeagueFeed.HomePage).context };
          cursor = page.nextCursor;
          more = page.hasMore;
          count++;
        }
        if (request.current !== own || currentScope.current !== scope) return;
        setEvents(all);
        setContext(allContext);
        setHasMore(more);
        setNextCursor(cursor);
        pages.current = count;
        restorePosition(position);
      } catch {
        if (request.current === own) setNotice(t('detail.feed.positionError'));
      } finally {
        if (request.current === own) {
          request.current = null;
          setRefreshing(false);
        }
      }
    })();
    return undefined;
  }, [
    leagueId,
    initialEvents,
    initialHasMore,
    initialNextCursor,
    initialError,
    initialContext,
    scope,
  ]);

  // Akış görünür alana girdi (kapsam başına bir kez; ana sayfa ya da lig sayfası).
  useEffect(() => {
    const node = root.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      trackConversion(leagueId.startsWith('home_') ? 'feed_view_home' : 'feed_view_league');
      observer.disconnect();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [scope]);

  useClientLayoutEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(
    () => () => {
      request.current?.controller.abort();
      freshTimers.current.forEach(timer => window.clearTimeout(timer));
    },
    [],
  );

  const markFresh = (ids: string[], kind: 'new' | 'page') => {
    if (!ids.length) return;
    setFresh(current => ({
      ...current,
      ...Object.fromEntries(ids.map((id, order) => [id, { kind, order }])),
    }));
    const timer = window.setTimeout(() => {
      setFresh(current => {
        const next = { ...current };
        ids.forEach(id => delete next[id]);
        return next;
      });
    }, FRESH_MS);
    freshTimers.current.push(timer);
  };

  useEffect(() => {
    const remember = () => {
      const position = capturePosition();
      if (position) saveFeedPosition(scope, position);
    };
    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('pagehide', remember);
    window.addEventListener('focusin', remember);
    return () => {
      window.removeEventListener('scroll', remember);
      window.removeEventListener('pagehide', remember);
      window.removeEventListener('focusin', remember);
    };
  }, [scope]);
  useEffect(() => {
    const controller = new AbortController();
    if (!viewer?.id || !reactionIds) {
      setReactionState({});
      return () => controller.abort();
    }
    setReactionError(false);
    let retryTimer = 0;
    const versions = new Map(reactionVersions.current);
    readReactions(reactionIds.split(','), controller.signal)
      .then(states => {
        if (!controller.signal.aborted && currentScope.current === scope)
          setReactionState(current => {
            const next = { ...current };
            Object.entries(states).forEach(([id, value]) => {
              if (!locks.current.has(id) && versions.get(id) === reactionVersions.current.get(id))
                next[id] = value;
            });
            return next;
          });
      })
      .then(() => {
        reactionAttempts.current = 0;
      })
      .catch(() => {
        if (controller.signal.aborted || currentScope.current !== scope) return;
        // Açılışta oturum/ağ hazır olmayabilir: 1, 3 ve 8 sn sonra kendiliğinden yeniden denenir.
        const delay = REACTION_RETRY_MS[reactionAttempts.current];
        reactionAttempts.current += 1;
        if (delay)
          retryTimer = window.setTimeout(() => setReactionRetry(value => value + 1), delay);
        else setReactionError(true);
      });
    return () => {
      controller.abort();
      window.clearTimeout(retryTimer);
    };
  }, [scope, reactionIds, reactionRetry]);

  // Uygulama arka plandan dönünce tebrik durumu tazelenir (başka cihazdaki değişiklik dahil).
  useEffect(() => {
    if (!viewer?.id) return undefined;
    let hiddenAt = 0;
    const onVisible = () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      else if (hiddenAt && Date.now() - hiddenAt > 30 * 1000) {
        reactionAttempts.current = 0;
        setReactionRetry(value => value + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [scope]);

  const readPage = async (cursor: string | null, signal: AbortSignal): Promise<LeagueFeed.Page> => {
    if (fetchPage) return fetchPage(cursor, signal);
    const params = new URLSearchParams({ leagueId, fresh: '1', ...(cursor ? { cursor } : {}) });
    const response = await fetch(`/api/league-feed?${params}`, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  };
  const { incoming, clearIncoming } = useFeedIncoming({
    scope,
    url: fetchPage
      ? '/api/feed-latest'
      : `/api/feed-latest?leagueId=${encodeURIComponent(leagueId)}`,
    events: visibleEvents,
    hidden: preferences.hidden,
    readFirst: signal => readPage(null, signal),
    paused: refreshing || loadingMore,
  });

  const refresh = async () => {
    if (request.current) return;
    const position = capturePosition();
    const own = { leagueId, controller: new AbortController() };
    request.current = own;
    setRefreshing(true);
    setRefreshError(false);
    setNotice('');
    try {
      let page = await readPage(null, own.controller.signal);
      let all = page.events;
      let allContext = (page as LeagueFeed.HomePage).context;
      let count = 1;
      while (
        page.hasMore &&
        page.nextCursor &&
        count < Math.min(pages.current, MAX_RESTORE_PAGES)
      ) {
        // eslint-disable-next-line no-await-in-loop -- eski sayfalar yeni imleçlerle yeniden doğrulanır
        page = await readPage(page.nextCursor, own.controller.signal);
        all = mergeFeedPages(all, page.events);
        if ((page as LeagueFeed.HomePage).context)
          allContext = { ...allContext, ...(page as LeagueFeed.HomePage).context };
        count++;
      }
      if (request.current !== own || currentScope.current !== scope) return;
      const previous = new Set(events.map(event => event.id));
      const addedIds = all.filter(event => !previous.has(event.id)).map(event => event.id);
      const added = addedIds.length;
      clearIncoming();
      markFresh(addedIds, 'new');
      setEvents(all);
      setContext(allContext);
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
      setFirstPageError(false);
      setLoadError(false);
      pages.current = count;
      setReactionState({});
      setReactionRetry(value => value + 1);
      trackConversion('feed_refreshed');
      setNotice(
        added ? t('detail.feed.refreshedNew', { count: added }) : t('detail.feed.refreshed'),
      );
      restorePosition(position);
    } catch {
      if (request.current === own) setRefreshError(true);
    } finally {
      if (request.current === own) {
        request.current = null;
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    if (reloadSignal) refresh();
  }, [reloadSignal]);

  usePullToRefresh(
    pullIndicator,
    () => {
      if (!request.current && !loadingMore && !pending.length) refresh();
    },
    !footer,
  );

  // Bekleyen yeni olaylar kullanıcı isteyince başa eklenir; akışın başına kaydırılır ve ilk yeni
  // karta odak verilir (düğme kaybolunca odak sayfaya düşmesin).
  const showIncoming = () => {
    if (!incoming || request.current) return;
    const { page, ids } = incoming;
    clearIncoming();
    setEvents(current => mergeFeedPages(current, page.events));
    if (page.context) setContext(current => ({ ...current, ...page.context }));
    markFresh(ids, 'new');
    setNotice(t('detail.feed.refreshedNew', { count: ids.length }));
    trackConversion('feed_incoming_shown');
    requestAnimationFrame(() => focusCard(ids, true));
  };

  // Kartı görünür alana getirir ve odak verir (gruplanmış kartta olay kimliğiyle de bulunur).
  const focusCard = (ids: string[], feedTop = false) => {
    if (!root.current) return;
    const card = Array.from(root.current.querySelectorAll<HTMLElement>('[data-feed-id]')).find(
      node => node.dataset.feedEvents?.split(' ').some(id => ids.includes(id)),
    );
    const top = (feedTop ? root.current : card)?.getBoundingClientRect().top;
    if (top !== undefined && (!feedTop || top < 0)) {
      window.scrollTo({
        top: window.scrollY + top - 16,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
    if (card) {
      card.setAttribute('tabindex', '-1');
      card.focus({ preventScroll: true });
    }
  };

  // Paylaşılan kart listede yoksa üstte ayrı gösterilir; sayfa açılınca karta gidilir.
  const sharedOutside =
    shared && !visibleEvents.some(event => event.id === shared.id) ? shared : null;
  useEffect(() => {
    if (!shared) return undefined;
    const frame = requestAnimationFrame(() => focusCard([shared.id]));
    return () => cancelAnimationFrame(frame);
  }, []);

  const membersById = useMemo(() => new Map(members.map(member => [member.id, member])), [members]);
  const sections = useMemo(
    () =>
      groupFeed(
        visibleEvents.filter(event => !preferences.hidden.has(event.id)),
        true,
        visibleContext,
      ),
    [visibleEvents, visibleContext, preferences.hidden],
  );
  const knownReactions = useMemo(
    () =>
      Object.fromEntries(
        visibleEvents
          .map(event => [event.id, reactionOf(event)] as const)
          .filter((entry): entry is readonly [string, FeedReaction] => !!entry[1]),
      ),
    [visibleEvents, reactionState, reactionOwner, scope, viewer?.id],
  );
  // S3: ana sayfada görülen kartlar (açılıştaki kayıt) sıralamada aşağı iner.
  const seenAtOpen = useFeedSeen(
    root,
    leagueId.startsWith('home_') ? viewer?.id : null,
    sections,
  );
  // Ç4: sabah 12:00'ye kadar dün geceki maçlar başta (saat yalnız tarayıcıda bilinir).
  const feedOrder = useMemo(() => {
    const items = sections.flatMap(section => section.items);
    if (!now) return { items, pinned: new Set<string>() };
    const night = pinLastNight(items, now);
    // Ana sayfa: kişiye özel sıralama (S1, gerekçe gösterilmez). Lig sayfası herkese aynı: çok
    // tebrik alan tek kart "Dün gece" kartlarının altına (etiketsiz, 5 Kas kararı).
    const ordered = leagueId.startsWith('home_')
      ? rankForViewer(night.items, now, visibleContext, night.pinned.size, seenAtOpen)
      : liftPopular(night.items, now, night.pinned.size).items;
    return { ...night, items: ordered };
  }, [sections, now, leagueId, visibleContext, seenAtOpen]);
  const viewerIsMember = !!viewer && viewer.lig_temel_id === leagueId;
  // C3: kartın öznesi sıralamada izleyiciye yakın (ana sayfada sunucu işareti, lig sayfasında üye sırası).
  const nearViewer = (event: LeagueFeed.Event) => {
    if (!viewer?.id || !REACTABLE.includes(event.tur)) return false;
    if (leagueId.startsWith('home_')) return visibleContext?.[event.id]?.yakin === true;
    const subject = feedSubjectIds(event)[0];
    return (
      viewerIsMember &&
      !!subject &&
      subject !== viewer.id &&
      isNearRank(
        membersById.get(viewer.id)?.piramit_sira ?? viewer.piramit_sira,
        membersById.get(subject)?.piramit_sira,
      )
    );
  };
  // LG-65: bu hafta katılan, izleyicinin henüz hoş geldin demediği oyuncular (kendi ligi).
  const newcomers = useMemo(() => {
    if (!viewer?.id) return [];
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const ownLeague = (event: LeagueFeed.Event) =>
      leagueId.startsWith('home_')
        ? visibleContext?.[event.id]?.source === 'league'
        : viewerIsMember;
    return visibleEvents.filter(
      event =>
        event.tur === 'katildi' &&
        event.olusturma >= since &&
        event.veri.oyuncu?.id !== viewer.id &&
        !preferences.hidden.has(event.id) &&
        ownLeague(event) &&
        reactionOf(event)?.selected === false,
    );
  }, [
    visibleEvents,
    visibleContext,
    reactionState,
    reactionOwner,
    scope,
    viewer?.id,
    preferences.hidden,
  ]);
  // LG-66: bu hafta oynanan, izleyicinin oynamadığı ve henüz tebrik etmediği maçlar (kendi ligi).
  const uncongratulated = useMemo(() => {
    if (!viewer?.id) return [];
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return (
      visibleEvents
        .filter(
          event =>
            event.tur === 'mac_sonucu' &&
            event.olusturma >= since &&
            event.veri.kazanan?.id !== viewer.id &&
            event.veri.kaybeden?.id !== viewer.id &&
            !preferences.hidden.has(event.id) &&
            (leagueId.startsWith('home_')
              ? visibleContext?.[event.id]?.source === 'league'
              : viewerIsMember) &&
            reactionOf(event)?.selected === false,
        )
        // Ç5: ilk lig maçı olan sonuçlar önce (yeni oyuncuya ilk karşılık), sonra sıralamada yakın
        // oyuncular (C3); sıra başka değişmez.
        .sort(
          (a, b) =>
            Number(!!b.veri.ilk_mac?.length) - Number(!!a.veri.ilk_mac?.length) ||
            Number(nearViewer(b)) - Number(nearViewer(a)),
        )
    );
  }, [
    membersById,
    visibleEvents,
    visibleContext,
    reactionState,
    reactionOwner,
    scope,
    viewer?.id,
    preferences.hidden,
  ]);
  // T7: akışın üstünde tek görev modülü. Öncelik: karşılık ver (seni tebrik edenler) > hoş geldin >
  // tebrik bekleyen maçlar. Seçim ziyaret boyunca sabit (biri bitince yerine başkası atlamaz, kayma
  // olmaz); haftalık özetteki düğme "tebrik bekleyenler"i öne alabilir.
  const returnRows = useMemo(
    () =>
      viewer?.id
        ? returnCongratsRows(
            visibleEvents.filter(event => !preferences.hidden.has(event.id)),
            viewer.id,
            event => knownReactions[event.id]?.selected,
          )
        : [],
    [visibleEvents, preferences.hidden, viewer?.id, knownReactions],
  );
  // B4: izleyicinin son 2 günde akışa düşen fotoğrafsız maçı (en düşük öncelikli görev).
  const storyEvent = viewer?.id
    ? visibleEvents.find(
        event =>
          event.tur === 'mac_sonucu' &&
          !event.veri.mac_foto_var_mi &&
          !!event.veri.mac_id &&
          Date.now() - event.olusturma < STORY_REMINDER_MS &&
          [event.veri.kazanan?.id, event.veri.kaybeden?.id].includes(viewer.id) &&
          !preferences.hidden.has(event.id),
      ) || null
    : null;
  const taskChoice = useRef<{ scope: string; task: FeedTask | null }>({ scope, task: null });
  if (taskChoice.current.scope !== scope) taskChoice.current = { scope, task: null };
  if (!taskChoice.current.task) {
    if (returnRows.length) taskChoice.current.task = 'inbox';
    else if (newcomers.length) taskChoice.current.task = 'welcome';
    else if (uncongratulated.length >= 2) taskChoice.current.task = 'catchup';
    else if (storyEvent) taskChoice.current.task = 'story';
  }
  const [taskOverride, setTaskOverride] = useState<{ scope: string; task: FeedTask } | null>(null);
  const task = taskOverride?.scope === scope ? taskOverride.task : taskChoice.current.task;
  const stripItems = useMemo((): PhotoStripItem[] => {
    if (!photoStrip) return [];
    const since = newestFeedTime(visibleEvents) - PHOTO_STRIP_DAYS * 24 * 3600 * 1000;
    return visibleEvents
      .filter(
        event =>
          event.olusturma >= since &&
          !preferences.hidden.has(event.id) &&
          !removedPhotoIds.includes(event.id) &&
          ((event.tur === 'mac_sonucu' && event.veri.mac_foto_var_mi) ||
            (event.tur === 'gonderi' && event.veri.foto_var_mi)),
      )
      .slice(0, PHOTO_STRIP_SIZE)
      .map(event => ({
        eventId: event.id,
        player: (event.tur === 'gonderi' ? event.veri.oyuncu : event.veri.kazanan)!,
      }))
      .filter(item => !!item.player);
  }, [photoStrip, visibleEvents, preferences.hidden, removedPhotoIds]);

  // İlk sayfa hatasında cursor'suz baştan, aksi halde sunucunun verdiği cursor'dan devam eder.
  const loadMore = async () => {
    if (request.current) return;
    const last = events[events.length - 1];
    const cursor = firstPageError ? null : nextCursor || (last ? encodeFeedCursor(last) : null);
    if (!firstPageError && !cursor) return;
    const own = { leagueId, controller: new AbortController() };
    request.current = own;
    setLoadingMore(true);
    setLoadError(false);
    try {
      const page = await readPage(cursor, own.controller.signal);
      if (request.current !== own) return;
      const pageContext = (page as Partial<LeagueFeed.HomePage>).context;
      if (pageContext)
        setContext(current => (cursor ? { ...current, ...pageContext } : pageContext));
      if (cursor) {
        const known = new Set(events.map(event => event.id));
        markFresh(
          page.events.filter(event => !known.has(event.id)).map(event => event.id),
          'page',
        );
      }
      setEvents(current => (cursor ? mergeFeedPages(current, page.events) : page.events));
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
      setFirstPageError(false);
      pages.current = cursor ? pages.current + 1 : 1;
    } catch {
      if (request.current === own) setLoadError(true);
    } finally {
      if (request.current === own) {
        request.current = null;
        setLoadingMore(false);
      }
    }
  };

  // Ana düğme alkışı açıp kapatır; seçiciden gelen tür aynıysa kapatır, farklıysa türü değiştirir.
  const toggleCongrats = async (
    event: LeagueFeed.Event,
    fromPhoto = false,
    kind?: ReactionKind,
  ): Promise<boolean> => {
    const ownState = reactionOf(event);
    if (!viewer?.id || !ownState || locks.current.has(event.id)) return false;
    locks.current.add(event.id);
    reactionVersions.current.set(event.id, (reactionVersions.current.get(event.id) || 0) + 1);
    setPending(current => [...current, event.id]);
    try {
      const result = await feedRequest<FeedReaction>('/api/feed-reactions', 'PUT', {
        eventId: event.id,
        selected: kind ? !(ownState.selected && ownState.kind === kind) : !ownState.selected,
        kind: kind || 'alkis',
      });
      if (currentScope.current === scope) {
        setReactionState(current => ({ ...current, [event.id]: result }));
        if (result.selected) setPopped({ id: event.id, key: Date.now(), photo: fromPhoto });
      }
      if (result.selected) trackConversion('feed_congrats');
      return true;
    } catch {
      if (currentScope.current === scope) showNotification('error', t('detail.feed.congratsError'));
      return false;
    } finally {
      if (currentScope.current === scope) {
        locks.current.delete(event.id);
        setPending(current => current.filter(id => id !== event.id));
      }
    }
  };

  // Kartın öznesi kendini tebrik edemez; maç kartında kaybeden "İyi maçtı" diyebilir (Ç2).
  const ownEvent = (event: LeagueFeed.Event) =>
    !!viewer?.id && feedSubjectIds(event).includes(viewer.id);

  // Fotoğrafa çift dokunma yalnız tebrik ekler (Instagram gibi); geri almak düğmeyle yapılır.
  // Zaten tebrik edildiyse yalnız kalp yeniden oynar; yeni tebrikte kalp sunucu onayından sonra.
  const photoCongrats = (event: LeagueFeed.Event) => {
    const mine = reactionOf(event);
    if (!viewer?.id || ownEvent(event) || !mine || refreshing) return;
    if (mine.selected) setPopped({ id: event.id, key: Date.now(), photo: true });
    else toggleCongrats(event, true);
  };
  // B4: giriş sonrası bekleyen tebrik (kart listede ya da paylaşılan kart olarak açıksa).
  useEffect(() => {
    if (!viewer?.id) return;
    const id = pendingCongratsIntent();
    if (!id) return;
    const event = [...visibleEvents, ...(shared ? [shared] : [])].find(item => item.id === id);
    if (!event) return;
    const state = reactionOf(event);
    if (ownEvent(event) || !REACTABLE.includes(event.tur)) {
      clearCongratsIntent();
      return;
    }
    if (!state || refreshing) return;
    clearCongratsIntent();
    if (!state.selected) toggleCongrats(event);
  }, [viewer?.id, visibleEvents, shared, reactionState, reactionOwner, refreshing]);

  const photoBurst = (event: LeagueFeed.Event) =>
    popped?.photo && popped.id === event.id ? popped.key : undefined;

  const monthLabel = (month = '') => {
    const [year, number] = month.split('-').map(Number);
    if (!year || !number) return month;
    return new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
      new Date(Date.UTC(year, number - 1, 15)),
    );
  };

  const renderName = (player?: LeagueFeed.Player, strong = false) => {
    if (!player) return null;
    const member = membersById.get(player.id);
    const name = playerName(member ? { ...player, ad: member.ad, soyad: member.soyad } : player);
    const content = strong ? <strong>{name}</strong> : <span>{name}</span>;
    // Ligde olan oyuncu mevcut profil modalını açar; ayrılmışsa düz metin kalır.
    if (viewer?.id && !(member && onSelectPlayer)) {
      return (
        <button
          type="button"
          className="league-feed__name"
          data-feed-focus={`name_${player.id}`}
          onClick={() => {
            trackConversion('feed_player_opened');
            setPreview({ id: player.id, scope });
          }}
        >
          {content}
        </button>
      );
    }
    return member && onSelectPlayer ? (
      <button
        type="button"
        className="league-feed__name"
        data-feed-focus={`name_${player.id}`}
        onClick={() => onSelectPlayer(member)}
      >
        {content}
      </button>
    ) : (
      <span className="league-feed__name league-feed__name--static">{content}</span>
    );
  };

  const renderAvatar = (player?: LeagueFeed.Player, size = 40) => {
    const photo = membersById.get(player?.id || '')?.foto_mini_url || player?.foto_mini_url;
    return (
      <Avatar size={size} src={storageImage(photo, size)} alt="">
        {[player?.ad, player?.soyad]
          .filter(Boolean)
          .map(part => part!.slice(0, 1).toLocaleUpperCase(locale))
          .join('') || '—'}
      </Avatar>
    );
  };

  // "Sen ve 3 kişi daha" / "Ali ve Ayşe": sayı sunucu sayacından, adlar son üç tebrikten.
  const reactorsText = (reactors: LeagueFeed.Reactor[], count: number) => {
    const ordered = [...reactors].sort(
      (a, b) => Number(b.id === viewer?.id) - Number(a.id === viewer?.id),
    );
    const label = (person: LeagueFeed.Reactor) =>
      person.id === viewer?.id
        ? t('detail.feed.reactors.you')
        : `${person.ad} ${person.soyad}`.trim() || '—';
    const others = count - 1;
    if (others <= 0) return label(ordered[0]);
    if (others === 1 && ordered[1])
      return t('detail.feed.reactors.two', { name: label(ordered[0]), other: label(ordered[1]) });
    return t('detail.feed.reactors.more', { name: label(ordered[0]), count: others });
  };

  // Kartın lig sayfası (ana sayfada olayın kendi ligi); paylaşım ve maç ayrıntısı bağlantısı buradan.
  const eventLeagueUrl = (event: LeagueFeed.Event) =>
    visibleContext?.[event.id]?.league.url ||
    (leagueId.startsWith('home_') ? null : convertTitleToUrl('ligler', '', leagueId));

  // LG-67: kart bağlantısı `?olay=` lig akışında kartı açar; önizleme metni ve görseli sunucuda.
  const share = (event: LeagueFeed.Event) => {
    const base = eventLeagueUrl(event);
    if (!base?.startsWith('/ligler/')) return null;
    const { veri } = event;
    const text =
      event.tur === 'mac_sonucu'
        ? t('detail.feed.matchWon', {
            winner: playerName(veri.kazanan),
            loser: playerName(veri.kaybeden),
            score: formatSets(veri.setler) || '—',
          })
        : event.tur === 'gonderi' && veri.metin
        ? veri.metin.slice(0, 140)
        : t('detail.feed.shareText');
    return (
      <LeagueShareButton
        className="league-feed__share"
        title="tenis.io"
        text={text}
        label={t('detail.feed.share')}
        path={`${base}?olay=${encodeURIComponent(event.id)}`}
        onShared={() => trackConversion('feed_share')}
      />
    );
  };

  const setPhotoFlag = (id: string, hasPhoto: boolean) => {
    const update = (item: LeagueFeed.Event) =>
      item.id === id ? { ...item, veri: { ...item.veri, mac_foto_var_mi: hasPhoto } } : item;
    setEvents(current => current.map(update));
    setShared(current => current && update(current));
  };

  const reaction = (event: LeagueFeed.Event) => {
    if (!REACTABLE.includes(event.tur)) return null;
    const own = ownEvent(event);
    const rival = isFeedRival(event, viewer?.id);
    const pop = popped?.id === event.id ? popped.key : 0;
    const mine = reactionOf(event);
    const done = !!viewer?.id && mine?.selected === true;
    // Yarı iyimser: düğme basılı görünümü hemen değişir; sayı ve kişiler sunucu onayıyla (Ç9).
    const waiting = pending.includes(event.id);
    const shown = waiting ? !done : done;
    const welcome = event.tur === 'katildi';
    const count = mine?.count ?? event.tebrik_sayisi;
    let kind = 'congrats';
    if (welcome) kind = 'welcome';
    else if (event.tur === 'gonderi') kind = 'like';
    else if (rival) kind = 'goodMatch';
    const label = t(`detail.feed.${kind}${shown ? 'Done' : ''}`);
    const countLabel = t('detail.feed.reactionCount', { count });
    const reactors = (mine?.preview ?? event.tebrik_onizleme ?? []).slice(0, 3);
    const counts = normalizeReactionCounts(mine?.counts ?? event.tepki_sayilari, count);
    // Tek tepki (alkış; D2). Eski 🔥/🎾 kayıtları varsa özet olarak gösterilmeye devam eder.
    const kinds =
      !welcome && topReactionKinds(counts).some(item => item !== 'alkis')
        ? topReactionKinds(counts)
        : [];
    return (
      <div className="league-feed__reaction">
        {viewer?.id && !own && (
          <Button
            type="text"
            size="small"
            className={`league-feed__react${shown ? ' is-active' : ''}`}
            aria-pressed={shown}
            aria-busy={waiting}
            aria-label={`${label} · ${countLabel}`}
            title={
              refreshing
                ? t('detail.feed.refreshing')
                : !mine
                ? t('detail.feed.reactionLoading')
                : label
            }
            disabled={!mine || refreshing}
            data-feed-focus={`reaction_${event.id}`}
            icon={
              <span key={pop} className={`league-feed__react-icon${pop ? ' is-popped' : ''}`}>
                {shown ? (
                  <LikeFilled className="league-feed__like" aria-hidden="true" />
                ) : (
                  <LikeOutlined className="league-feed__like" aria-hidden="true" />
                )}
              </span>
            }
            onClick={() => toggleCongrats(event)}
          >
            {count > 0 && (
              <span key={pop} className={pop ? 'league-feed__count-roll' : undefined}>
                {count.toLocaleString(locale)}
              </span>
            )}
          </Button>
        )}
        {kinds.length > 0 && (
          <span className="league-feed__kinds">
            <span aria-hidden="true">{kinds.map(item => REACTION_EMOJI[item]).join('')}</span>
            <span className="visually-hidden">
              {kinds
                .map(item => `${t(`detail.feed.kinds.${item}`)}: ${counts[item] || 0}`)
                .join(', ')}
            </span>
          </span>
        )}
        {!viewer?.id && loginToCongrats(event, label, countLabel, count)}
        {own && count > 0 && (
          <span className="league-feed__count" aria-label={countLabel} title={countLabel}>
            <LikeOutlined className="league-feed__like" aria-hidden="true" />
            {count.toLocaleString(locale)}
          </span>
        )}
        {/* Tebrik edenler şeridin sağında yalnız yüzlerle; adlar dokununca açılır. */}
        {count > 0 && reactors.length > 0 && (
          <Popover
            trigger="click"
            placement="topRight"
            content={
              <span className="league-feed__reactors-text">{reactorsText(reactors, count)}</span>
            }
          >
            <button
              type="button"
              className="league-feed__reactors"
              aria-label={reactorsText(reactors, count)}
            >
              <span className="league-feed__reactor-faces" aria-hidden="true">
                {reactors.map(person => (
                  <span key={person.id}>
                    {renderAvatar({ ...person, null_seviye: null, verified_account: false }, 20)}
                  </span>
                ))}
              </span>
            </button>
          </Popover>
        )}
      </div>
    );
  };

  // B4: girişsiz ziyaretçi için tebrik düğmesi giriş sayfasına götürür; giriş sonrası aynı kart açılır
  // ve tebrik bir kez, sunucu onayıyla kaydedilir (niyet yalnız bu sekmede).
  const loginToCongrats = (
    event: LeagueFeed.Event,
    label: string,
    countLabel: string,
    count: number,
  ) => {
    const base = eventLeagueUrl(event);
    if (!base?.startsWith('/ligler/')) return null;
    const target = `${base}?olay=${encodeURIComponent(event.id)}`;
    return (
      <Link href={`${ROUTE_LOGIN}?redirect=${encodeURIComponent(target)}`} passHref>
        <Button
          type="text"
          size="small"
          className="league-feed__react"
          aria-label={`${t('detail.feed.loginToCongrats')} · ${countLabel}`}
          title={t('detail.feed.loginToCongrats')}
          onClick={() => {
            rememberCongratsIntent(event.id);
            trackConversion('feed_login_to_congrats');
          }}
          icon={
            <span className="league-feed__react-icon">
              <LikeOutlined className="league-feed__like" aria-hidden="true" />
            </span>
          }
        >
          {count > 0 && <span>{count.toLocaleString(locale)}</span>}
        </Button>
      </Link>
    );
  };

  // D1: izleyicinin kendi ligi mi (lig sayfasında üyelik, ana sayfada olayın kaynağı).
  const ownLeagueEvent = (event: LeagueFeed.Event) =>
    leagueId.startsWith('home_') ? visibleContext?.[event.id]?.source === 'league' : viewerIsMember;

  // D1: sezon kartı (başladı/bitiyor) lig üyesini maç teklifine götürür.
  const seasonAction = (event: LeagueFeed.Event) =>
    viewer?.id && ownLeagueEvent(event) ? (
      <Link href={ROUTE_PLAY} passHref>
        <Button className="league-feed__offer">
          <PlusCircleOutlined aria-hidden="true" />
          <span>{t('detail.actions.proposeMatch')}</span>
        </Button>
      </Link>
    ) : null;

  // D1: ayın aktifleri kartında birinci oyuncunun tebrik edilmemiş son kartı tek dokunuşla tebrik edilir.
  const monthlyAction = (event: LeagueFeed.Event) => {
    const top = event.veri.oyuncular?.[0]?.oyuncu;
    if (!viewer?.id || !top || top.id === viewer.id || !ownLeagueEvent(event)) return null;
    const target = visibleEvents.find(
      item =>
        REACTABLE.includes(item.tur) &&
        feedSubjectIds(item).includes(top.id) &&
        !ownEvent(item) &&
        !preferences.hidden.has(item.id) &&
        reactionOf(item) !== undefined,
    );
    const state = target && reactionOf(target);
    if (!target || !state) return null;
    if (state.selected)
      return (
        <span className="league-feed__welcomed" role="status">
          <CheckOutlined aria-hidden="true" /> {t('detail.feed.monthlyCongratsDone')}
        </span>
      );
    return (
      <Button
        className="league-feed__offer"
        loading={pending.includes(target.id)}
        disabled={refreshing}
        onClick={() => toggleCongrats(target)}
      >
        <LikeOutlined aria-hidden="true" />
        <span>{t('detail.feed.monthlyCongrats')}</span>
      </Button>
    );
  };

  // B2: partner ilanı kartı; süresi geçince belirtilir (saat yalnız tarayıcıda).
  const partnerExpiredNow = (event: LeagueFeed.Event) =>
    !!event.veri.partner && !!now && partnerExpired(event.veri.partner, now);
  // "Cumartesi, 8 Kas · Akşam" / "Hafta sonu, 8–9 Kas · Akşam" (ilan günleri İstanbul takvimi).
  const partnerWhen = (partner: NonNullable<LeagueFeed.Event['veri']['partner']>) => {
    const date = (value: string) => new Date(`${value}T12:00:00Z`);
    const dayMonth = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    const day =
      partner.gun === 'hafta_sonu'
        ? `${t('detail.feed.partnerWeekend')}, ${[partner.tarih, partner.bitis]
            .map(value => dayMonth.format(date(value)))
            .join('–')}`
        : `${new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(
            date(partner.tarih),
          )}, ${dayMonth.format(date(partner.tarih))}`;
    return `${day} · ${t(`detail.posts.partner.zaman.${partner.zaman}`)}`;
  };
  // Teklif: aynı ligdeki diğer oyuncu, süresi geçmemiş ilan; teklif ekranı ilanın gününü seçili açar.
  const partnerOffer = (event: LeagueFeed.Event): PartnerOffer => {
    const author = event.veri.oyuncu!;
    const partner = event.veri.partner!;
    if (viewer?.id === author.id) return { reason: 'own' };
    if (partnerExpiredNow(event)) return { reason: 'expired' };
    if (!viewer?.id) return { reason: 'guest' };
    if (!ownLeagueEvent(event)) return { reason: 'outside' };
    if (cannotOffers.includes(author.id)) return { reason: 'pending' };
    const query = new URLSearchParams({
      tarih: partner.tarih,
      bitis: partner.bitis,
      zaman: partner.zaman,
    });
    return { href: `${ROUTE_PLAY}/${author.id}?${query}` };
  };
  const partnerCard = (event: LeagueFeed.Event) => {
    const { veri } = event;
    if (!veri.partner || !veri.oyuncu) return null;
    return (
      <FeedPartnerCard
        avatar={renderAvatar(veri.oyuncu, 56)}
        largeAvatar={renderAvatar(veri.oyuncu, 64)}
        name={playerName(veri.oyuncu)}
        firstName={veri.oyuncu.ad}
        level={veri.oyuncu.null_seviye}
        when={partnerWhen(veri.partner)}
        league={visibleContext?.[event.id]?.league.name || leagueName || null}
        note={veri.metin || null}
        expired={partnerExpiredNow(event)}
        offer={partnerOffer(event)}
      />
    );
  };

  // Ç6: kaybeden oyuncuya kazananla rövanş kısayolu (mevcut teklif sayfası; uygunluğu orası denetler).
  const rematchAction = (event: LeagueFeed.Event) => {
    const winner = event.veri.kazanan;
    if (!winner || !isFeedRival(event, viewer?.id) || cannotOffers.includes(winner.id)) return null;
    const ownLeague = leagueId.startsWith('home_')
      ? visibleContext?.[event.id]?.source === 'league'
      : viewerIsMember && membersById.has(winner.id);
    if (!ownLeague) return null;
    return (
      <Link href={`${ROUTE_PLAY}/${winner.id}`} passHref>
        <Button
          className="league-feed__offer"
          onClick={() => trackConversion('feed_rematch_opened')}
        >
          <PlusCircleOutlined aria-hidden="true" />
          <span>{t('detail.feed.rematch')}</span>
        </Button>
      </Link>
    );
  };

  // K3(a): yorum yerine maç notları; sayı notlara götürür, notu olmayan oyuncuda yazmayı açar.
  const notesAction = (event: LeagueFeed.Event) => {
    const notes = event.veri.notlar || [];
    const participant =
      !!viewer?.id && [event.veri.kazanan?.id, event.veri.kaybeden?.id].includes(viewer.id);
    const canWrite = participant && !!now && event.olusturma + MATCH_NOTE_WINDOW_MS > now;
    if (!event.veri.mac_id || (!notes.length && !canWrite)) return null;
    const label = t('detail.feed.notesAction', { count: notes.length });
    return (
      <Button
        type="text"
        size="small"
        className="league-feed__react league-feed__notes"
        aria-label={label}
        title={label}
        icon={<MessageOutlined aria-hidden="true" />}
        onClick={click => {
          trackConversion('feed_notes_open');
          const list = (click.currentTarget as HTMLElement)
            .closest('li')
            ?.querySelector<HTMLElement>('.match-notes__list');
          if (notes.length && list) {
            list.scrollIntoView({
              block: 'nearest',
              behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
            return;
          }
          setNoteSignal(current => ({ ...current, [event.id]: (current[event.id] || 0) + 1 }));
        }}
      >
        {notes.length > 0 && <span>{notes.length.toLocaleString(locale)}</span>}
      </Button>
    );
  };

  const offerAction = (player?: LeagueFeed.Player) => {
    if (!player || !viewerIsMember || player.id === viewer?.id) return null;
    if (!membersById.has(player.id) || cannotOffers.includes(player.id)) return null;
    return (
      <Link href={`${ROUTE_PLAY}/${player.id}`} passHref>
        <Button className="league-feed__offer">
          <PlusCircleOutlined aria-hidden="true" />
          <span>{t('detail.actions.proposeMatch')}</span>
        </Button>
      </Link>
    );
  };

  // Son 7 gün göreli ("Bugün 18:00", "Dün 18:00", "Pzt 18:00"), daha eskisi tam tarih (T2). Göreli
  // biçim yalnız tarayıcıda (ilk çizim sunucuyla aynı kalsın); tam tarih her zaman `title`da.
  const time = (ms: number) => {
    const full = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Istanbul',
    }).format(ms);
    let text = full;
    if (now && ms <= now && now - ms < 7 * 24 * 3600 * 1000) {
      const clock = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul',
      }).format(ms);
      const day = feedDay(ms);
      if (day === feedDay(now)) text = t('detail.feed.timeToday', { time: clock });
      else if (day === feedDay(now - 24 * 3600 * 1000))
        text = t('detail.feed.timeYesterday', { time: clock });
      else
        text = new Intl.DateTimeFormat(locale, {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Istanbul',
        }).format(ms);
    }
    return (
      <time className="league-feed__time" dateTime={new Date(ms).toISOString()} title={full}>
        {text}
      </time>
    );
  };

  // Çeviri metnindeki ad gibi öğeler `{{ad}}` yerine işaretle girer; sonra React öğesiyle değiştirilir.
  const sentence = (key: string, values: Record<string, React.ReactNode>) => {
    const nodes: Record<string, React.ReactNode> = {};
    const plain: Record<string, string | number> = {};
    Object.entries(values).forEach(([name, value]) => {
      if (typeof value === 'string' || typeof value === 'number') plain[name] = value;
      else {
        nodes[name] = value;
        plain[name] = sentenceToken(name);
      }
    });
    return splitSentence(t(key, plain)).map((part, index) => (
      // eslint-disable-next-line react/no-array-index-key -- cümle parçalarının sırası sabit
      <React.Fragment key={index}>
        {typeof part === 'string' ? part : nodes[part.token]}
      </React.Fragment>
    ));
  };

  const nameList = (players: LeagueFeed.Player[]) => {
    const tokens = players.map((_, index) => sentenceToken(`n${index}`));
    const text = new Intl.ListFormat(locale, { type: 'conjunction' }).format(tokens);
    return splitSentence(text).map((part, index) => (
      // eslint-disable-next-line react/no-array-index-key -- liste parçalarının sırası sabit
      <React.Fragment key={index}>
        {typeof part === 'string' ? part : renderName(players[Number(part.token.slice(1))], true)}
      </React.Fragment>
    ));
  };

  const verifiedName = (player?: LeagueFeed.Player) => (
    <>
      {renderName(player, true)}
      <VerifiedAccount verifiedAccount={player?.verified_account} />
    </>
  );

  const icon = (node: React.ReactNode) => <span className="league-feed__icon">{node}</span>;

  const card = (
    leading: React.ReactNode,
    text: React.ReactNode,
    event: { olusturma: number; veri?: Pick<LeagueFeed.Event['veri'], 'mac_saati'> } | null,
    extra?: React.ReactNode,
    actions?: React.ReactNode,
    // Bağlamsal eylem (maç teklifi, rövanş) simge şeridinin altında ayrı satır.
    cta?: React.ReactNode,
  ) => (
    <div className="league-feed__row">
      {leading}
      <div className="league-feed__body">
        <p className="league-feed__sentence">{text}</p>
        {extra}
        <div className="league-feed__meta">
          {/* Maç kartında oynandığı saat; diğer kartlarda paylaşım zamanı. */}
          {event && time(event.veri?.mac_saati || event.olusturma)}
          {actions && <div className="league-feed__actions">{actions}</div>}
          {cta && <div className="league-feed__cta">{cta}</div>}
        </div>
      </div>
    </div>
  );

  // Maç kartı bağlamı: hikâye, ilk lig maçı (Ç5), ligdeki ikili geçmiş ve rakibin tebriki (Ç2, Ç6).
  const matchExtras = (event: LeagueFeed.Event) => {
    const { veri } = event;
    const mine = reactionOf(event);
    const loserId = veri.kaybeden?.id;
    const rivalCongrats =
      !!loserId &&
      (event.tebrik_edenler.includes(loserId) ||
        (mine?.preview ?? event.tebrik_onizleme ?? []).some(person => person.id === loserId)) &&
      !(loserId === viewer?.id && mine?.selected === false);
    const firsts = (veri.ilk_mac || []).filter(id => [veri.kazanan?.id, loserId].includes(id));
    let first: string | null = null;
    if (firsts.length > 1) first = t('detail.feed.firstMatchBoth');
    else if (firsts.length === 1)
      first = t('detail.feed.firstMatch', {
        name: playerName(firsts[0] === loserId ? veri.kaybeden : veri.kazanan),
      });
    const lines = [
      veri.hikaye
        ? t(`detail.feed.story.${veri.hikaye.tur}`, {
            loser: playerName(veri.kaybeden),
            count: veri.hikaye.sayi,
          })
        : null,
      first,
    ].filter(Boolean);
    const notes = [
      veri.ikili
        ? t('detail.feed.headToHead', {
            winnerWins: veri.ikili.kazanan,
            loserWins: veri.ikili.kaybeden,
          })
        : null,
      rivalCongrats ? t('detail.feed.rivalCongrats') : null,
    ].filter(Boolean);
    if (!lines.length && !notes.length) return null;
    return (
      <>
        {lines.map(line => (
          <p key={line} className="league-feed__story">
            {line}
          </p>
        ))}
        {notes.length > 0 && <p className="league-feed__note-line">{notes.join(' · ')}</p>}
      </>
    );
  };

  const renderEvent = (event: LeagueFeed.Event) => {
    const { veri } = event;
    switch (event.tur) {
      case 'katildi':
        return card(
          renderAvatar(veri.oyuncu),
          veri.oyuncu?.null_seviye
            ? sentence('detail.feed.joinedLevel', {
                name: verifiedName(veri.oyuncu),
                level: veri.oyuncu.null_seviye,
              })
            : sentence('detail.feed.joined', { name: verifiedName(veri.oyuncu) }),
          event,
          null,
          <>
            {reaction(event)}
            {share(event)}
          </>,
          offerAction(veri.oyuncu),
        );
      case 'mac_sonucu': {
        const sets = formatSets(veri.setler);
        const names = { winner: renderName(veri.kazanan, true), loser: renderName(veri.kaybeden) };
        const leagueUrl = eventLeagueUrl(event);
        const matchUrl =
          veri.mac_id && leagueUrl?.startsWith('/ligler/')
            ? `${leagueUrl}?mac=${encodeURIComponent(veri.mac_id)}`
            : null;
        // Skor maç ayrıntısına götürür (ayrı "Maçı gör" bağlantısı yerine).
        const score =
          sets && matchUrl ? (
            <Link href={matchUrl}>
              <a
                href={matchUrl}
                className="league-feed__score-link"
                title={t('detail.feed.matchDetails')}
                onClick={() => trackConversion('feed_match_opened')}
                data-feed-focus={`match_${event.id}`}
              >
                {sets}
              </a>
            </Link>
          ) : (
            sets
          );
        let text;
        if (!sets) text = sentence('detail.feed.matchWonNoScore', names);
        else if (veri.puan)
          text = sentence('detail.feed.matchWonPoints', { ...names, score, points: veri.puan });
        else text = sentence('detail.feed.matchWon', { ...names, score });
        const photo = !removedPhotoIds.includes(event.id) ? (
          <MatchPhoto
            key={event.id}
            event={event}
            viewer={viewer}
            hero
            burst={photoBurst(event)}
            onDoubleTap={REACTABLE.includes(event.tur) ? () => photoCongrats(event) : undefined}
            onRemoved={id => {
              setEvents(current =>
                current.map(item =>
                  item.id === id
                    ? { ...item, veri: { ...item.veri, mac_foto_var_mi: false } }
                    : item,
                ),
              );
              onPhotoRemoved?.(id);
            }}
          />
        ) : null;
        // Skorsuz (hükmen) kartta ayrıntı bağlantısı şeritte kalır.
        const details =
          !sets && matchUrl ? (
            <Link href={matchUrl}>
              <a
                href={matchUrl}
                className="league-feed__detail"
                onClick={() => trackConversion('feed_match_opened')}
                data-feed-focus={`match_${event.id}`}
              >
                {t('detail.feed.matchDetails')}
              </a>
            </Link>
          ) : null;
        // Instagram düzeni: fotoğraf üstte, altında skor cümlesi ve oyuncu notları.
        return (
          <>
            {photo}
            {card(
              renderAvatar(veri.kazanan),
              text,
              event,
              matchExtras(event),
              <>
                {reaction(event)}
                {notesAction(event)}
                {share(event)}
                {details}
              </>,
              rematchAction(event),
            )}
            <MatchNotes
              editSignal={noteSignal[event.id] || 0}
              onNotesChange={notes => {
                const update = (item: LeagueFeed.Event) =>
                  item.id === event.id ? { ...item, veri: { ...item.veri, notlar: notes } } : item;
                setEvents(current => current.map(update));
                setShared(current => current && update(current));
              }}
              key={event.id}
              event={event}
              viewer={viewer}
              renderName={player => renderName(player, true)}
            />
            <FeedMatchStory
              event={event}
              viewer={viewer}
              onPhotoChange={setPhotoFlag}
              onAddNote={() =>
                setNoteSignal(current => ({ ...current, [event.id]: (current[event.id] || 0) + 1 }))
              }
            />
          </>
        );
      }
      case 'gonderi': {
        if (veri.partner && veri.oyuncu) {
          return (
            <>
              {partnerCard(event)}
              <div className="league-feed__meta league-feed__meta--partner">
                {time(event.olusturma)}
                <div className="league-feed__actions">
                  {reaction(event)}
                  {share(event)}
                  <FeedPostMenu
                    event={event}
                    viewer={viewer}
                    onRemoved={id => setEvents(current => current.filter(item => item.id !== id))}
                  />
                </div>
              </div>
            </>
          );
        }
        // Instagram düzeni: fotoğraf (yoksa oynatılabilir video) kart genişliğinde üstte; altında
        // yazan, yazı ve kalan bağlantı.
        const videoOnTop = !veri.foto_var_mi && !!veri.baglanti && !!feedVideoEmbed(veri.baglanti);
        return (
          <>
            {veri.foto_var_mi && (
              <FeedPostPhoto
                event={event}
                burst={photoBurst(event)}
                onDoubleTap={() => photoCongrats(event)}
              />
            )}
            {videoOnTop && veri.baglanti && (
              <div className="feed-post__media">
                <FeedPostLink link={veri.baglanti} />
              </div>
            )}
            {card(
              renderAvatar(veri.oyuncu),
              verifiedName(veri.oyuncu),
              event,
              <>
                {veri.metin && <FeedPostText text={veri.metin} />}
                {veri.baglanti && !videoOnTop && <FeedPostLink link={veri.baglanti} />}
              </>,
              <>
                {reaction(event)}
                {share(event)}
                <FeedPostMenu
                  event={event}
                  viewer={viewer}
                  onRemoved={id => setEvents(current => current.filter(item => item.id !== id))}
                />
              </>,
            )}
          </>
        );
      }
      case 'kilometre_tasi':
        return card(
          renderAvatar(veri.oyuncu),
          veri.tip === 'ilk_galibiyet'
            ? sentence('detail.feed.firstWin', { name: renderName(veri.oyuncu, true) })
            : sentence('detail.feed.nthMatch', {
                name: renderName(veri.oyuncu, true),
                count: veri.sayi ?? '—',
              }),
          event,
          null,
          <>
            {reaction(event)}
            {share(event)}
          </>,
        );
      case 'haftalik_ozet': {
        const parts = [
          typeof veri.mac_sayisi === 'number'
            ? t('detail.feed.weeklyMatches', { count: veri.mac_sayisi })
            : '',
          typeof veri.yeni_oyuncu_sayisi === 'number'
            ? t('detail.feed.weeklyJoins', { count: veri.yeni_oyuncu_sayisi })
            : '',
          // Topluluk normu (Ç8): son 7 günün kartlarına verilen tebrik.
          veri.verilen_tebrik
            ? t('detail.feed.weeklyCongrats', { count: veri.verilen_tebrik })
            : '',
        ].filter(Boolean);
        // Tek eylem: izleyicinin tebrik bekleyen maçları varsa listeye götürür.
        const catchUp =
          uncongratulated.length >= 2 ? (
            <Button
              className="league-feed__offer"
              onClick={() => {
                setTaskOverride({ scope, task: 'catchup' });
                requestAnimationFrame(() =>
                  document
                    .getElementById('feed-catchup-title')
                    ?.closest('section')
                    ?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' }),
                );
              }}
            >
              <LikeOutlined aria-hidden="true" />
              <span>{t('detail.feed.weeklyCatchUp', { count: uncongratulated.length })}</span>
            </Button>
          ) : null;
        // B3: haftanın en çok tebrik alan maçı; kart listedeyse ona kaydırır, değilse paylaşım bağlantısı.
        const best = veri.haftanin_maci;
        const bestBase = best ? eventLeagueUrl(event) : null;
        const bestInList = !!best && visibleEvents.some(item => item.id === best.olay_id);
        const weeklyMatch = best ? (
          <div className="league-feed__weekly-match">
            <p className="league-feed__subtitle">
              <span aria-hidden="true">🏆</span> {t('detail.feed.weeklyMatchLead')}
            </p>
            <p className="league-feed__line">
              {sentence('detail.feed.weeklyMatchLine', {
                winner: renderName(best.kazanan, true),
                loser: renderName(best.kaybeden, true),
                score: (
                  <span className="league-feed__nowrap">{formatSets(best.setler) || '—'}</span>
                ),
                count: best.tebrik_sayisi,
              })}
            </p>
            {bestInList ? (
              <Button
                type="link"
                className="league-feed__weekly-match-open"
                onClick={() => {
                  trackConversion('feed_weekly_match_open');
                  focusCard([best.olay_id]);
                }}
              >
                {t('detail.feed.weeklyMatchOpen')}
              </Button>
            ) : bestBase?.startsWith('/ligler/') ? (
              <Link href={`${bestBase}?olay=${encodeURIComponent(best.olay_id)}`} passHref>
                <Button
                  type="link"
                  className="league-feed__weekly-match-open"
                  onClick={() => trackConversion('feed_weekly_match_open')}
                >
                  {t('detail.feed.weeklyMatchOpen')}
                </Button>
              </Link>
            ) : null}
          </div>
        ) : null;
        return card(
          icon(<CalendarOutlined aria-hidden="true" />),
          parts.length ? parts.join(' ') : t('detail.feed.weeklyQuiet'),
          event,
          weeklyMatch || veri.yukselenler?.length ? (
            <>
              {weeklyMatch}
              {veri.yukselenler?.length ? (
                <>
                  <p className="league-feed__subtitle">{t('detail.feed.weeklyMoversLead')}</p>
                  <ul className="league-feed__ranking">
                    {veri.yukselenler.map(mover => (
                      <li key={mover.oyuncu.id}>
                        {renderAvatar(mover.oyuncu, 28)}
                        <span className="league-feed__line">
                          {sentence('detail.feed.mover', {
                            name: renderName(mover.oyuncu, true),
                            count: mover.fark,
                            rank: mover.sira,
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          ) : null,
          catchUp,
        );
      }
      case 'ayin_aktifleri':
        return card(
          icon(<FlagOutlined aria-hidden="true" />),
          t('detail.feed.monthlyLead', { month: monthLabel(veri.ay) }),
          event,
          <ul className="league-feed__ranking">
            {veri.oyuncular?.map(item => (
              <li key={item.oyuncu.id}>
                {renderAvatar(item.oyuncu, 28)}
                <span className="league-feed__line">
                  {sentence('detail.feed.monthlyPlayer', {
                    name: renderName(item.oyuncu, true),
                    count: item.mac_sayisi,
                  })}
                </span>
              </li>
            ))}
          </ul>,
          monthlyAction(event),
        );
      case 'sezon': {
        let text;
        if (veri.durum === 'bitiyor')
          text = t('detail.feed.seasonEnding', { count: veri.kalan_gun ?? 7 });
        else if (veri.bitis)
          text = t('detail.feed.seasonStarted', {
            date: getDate(veri.bitis / 1000, i18n.language),
          });
        else text = t('detail.feed.seasonStartedNoDate');
        return card(
          icon(<CalendarOutlined aria-hidden="true" />),
          text,
          event,
          null,
          seasonAction(event),
        );
      }
      // LG-61: kulüp yetkilisinin süreli duyurusu (yalnız hedef ligde, yayın–bitiş arasında).
      case 'duyuru': {
        const { duyuru, yazan, klup } = veri;
        if (!duyuru || !yazan || !klup) return null;
        const until = new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Istanbul',
        }).format(duyuru.bitis);
        return card(
          icon(<NotificationOutlined aria-hidden="true" />),
          sentence('detail.feed.announcement.title', {
            club: (
              <Link href={convertTitleToUrl('kulupler', klup.ad, klup.id)}>
                <a className="league-feed__link">{klup.ad}</a>
              </Link>
            ),
          }),
          event,
          <>
            <p className="feed-announcement__text">{duyuru.metin}</p>
            {duyuru.baglanti && <FeedPostLink link={duyuru.baglanti} />}
            <p className="feed-announcement__meta">
              {t('detail.feed.announcement.by', {
                name: `${yazan.ad} ${yazan.soyad}`.trim() || '—',
                role: t(`detail.feed.announcement.role.${yazan.rol}`),
              })}
              {' · '}
              {t('detail.feed.announcement.until', { date: until })}
            </p>
          </>,
          <FeedAnnouncementMenu event={event} viewer={viewer} />,
        );
      }
      case 'kulup': {
        const link = (prefix: string, ref?: LeagueFeed.Ref) =>
          ref ? (
            <Link href={convertTitleToUrl(prefix, ref.ad, ref.id)}>
              <a className="league-feed__link">{ref.ad}</a>
            </Link>
          ) : null;
        const club = link('kulupler', veri.klup);
        if (veri.olay === 'yeni_antrenor') {
          return card(
            renderAvatar(veri.antrenor),
            sentence(
              veri.rol === 'bas' ? 'detail.feed.clubNewHeadCoach' : 'detail.feed.clubNewCoach',
              {
                name: renderName(veri.antrenor, true),
                club,
              },
            ),
            event,
          );
        }
        return card(
          icon(<ApartmentOutlined aria-hidden="true" />),
          veri.olay === 'yeni_lig'
            ? sentence('detail.feed.clubNewLeague', { club, league: link('ligler', veri.lig) })
            : sentence('detail.feed.clubNewCourt', { club, court: link('kortlar', veri.kort) }),
          event,
        );
      }
      case 'turnuva': {
        const tournament = veri.turnuva ? (
          <Link href={convertTitleToUrl('turnuvalar', veri.turnuva.ad, veri.turnuva.id)}>
            <a className="league-feed__link">{veri.turnuva.ad}</a>
          </Link>
        ) : null;
        let text;
        if (veri.olay === 'kura_yayinlandi')
          text = sentence('detail.feed.tournamentDraw', { tournament });
        else if (veri.olay === 'tamamlandi')
          text = sentence('detail.feed.tournamentCompleted', { tournament });
        else if (veri.basvuru_bitis) {
          text = sentence('detail.feed.tournamentOpenUntil', {
            tournament,
            date: getDate(veri.basvuru_bitis / 1000, i18n.language),
          });
        } else text = sentence('detail.feed.tournamentOpen', { tournament });
        const champions =
          veri.olay === 'tamamlandi' && veri.sampiyonlar?.length ? (
            <ul className="league-feed__ranking">
              {veri.sampiyonlar.map(item => (
                <li key={`${item.kategori || ''}-${item.ad}`}>
                  <TrophyOutlined aria-hidden="true" className="league-feed__up" />
                  <span className="league-feed__line">
                    {item.kategori
                      ? t('detail.feed.championIn', { category: item.kategori, name: item.ad })
                      : t('detail.feed.champion', { name: item.ad })}
                  </span>
                </li>
              ))}
            </ul>
          ) : null;
        return card(icon(<TrophyOutlined aria-hidden="true" />), text, event, champions);
      }
      default:
        return null;
    }
  };

  // Ana sayfa akışında olayın hangi ligden ve neden geldiği (kendi ligi, takip, kulüp, şehir).
  const renderContext = (event: LeagueFeed.Event) => {
    const item = visibleContext?.[event.id];
    if (!item) return null;
    const cheered = item.tebrik_eden;
    return (
      <>
        {cheered && (
          <p className="league-feed__social">
            {sentence('detail.feed.cheeredBy', {
              name: <strong>{`${cheered.ad} ${cheered.soyad}`.trim()}</strong>,
            })}
          </p>
        )}
        <p className={`league-feed__context league-feed__context--${item.source}`}>
          <span className="league-feed__source">{t(`detail.feed.source.${item.source}`)}</span>
          {item.league.name && (
            <Link href={item.league.url}>
              <a className="league-feed__league">{item.league.name}</a>
            </Link>
          )}
        </p>
      </>
    );
  };

  // LG-69: toplu katılım kartından da hoş geldin (her katılıma mevcut tebrik API'siyle, sırayla).
  const joinWelcome = (item: Extract<LeagueFeed.Item, { kind: 'joins' }>) => {
    if (!viewer?.id) return null;
    const ownLeague = leagueId.startsWith('home_')
      ? visibleContext?.[item.events[0].id]?.source === 'league'
      : viewerIsMember;
    const targets = ownLeague
      ? item.events.filter(event => event.veri.oyuncu?.id !== viewer.id && reactionOf(event))
      : [];
    if (!targets.length) return null;
    const open = targets.filter(event => !reactionOf(event)!.selected);
    if (!open.length) {
      return (
        <span className="league-feed__welcomed" role="status">
          <CheckOutlined aria-hidden="true" /> {t('detail.feed.welcomeDone')}
        </span>
      );
    }
    const busy = targets.some(event => pending.includes(event.id));
    return (
      <Button
        className="league-feed__offer"
        loading={busy}
        onClick={async () => {
          // eslint-disable-next-line no-restricted-syntax, no-await-in-loop -- sıralı istek bilerek
          for (const event of open) await toggleCongrats(event);
        }}
      >
        {t('detail.feed.welcomeCount', { count: open.length })}
      </Button>
    );
  };

  const renderJoins = (item: Extract<LeagueFeed.Item, { kind: 'joins' }>) =>
    card(
      icon(<UserAddOutlined aria-hidden="true" />),
      sentence(`detail.feed.${item.players.length > 3 ? 'joinedGroupMore' : 'joinedGroup'}`, {
        count: item.players.length,
        more: item.players.length - 3,
        names: <>{nameList(item.players.slice(0, 3))}</>,
      }),
      item,
      null,
      joinWelcome(item),
    );

  const changePreference = async (ids: string[], hidden: boolean, all = false) => {
    await preferences.change(ids, hidden, all);
    requestAnimationFrame(() => {
      if (currentScope.current !== scope) return;
      const restored =
        !hidden &&
        Array.from(root.current?.querySelectorAll<HTMLElement>('[data-feed-id]') || []).find(
          node => all || node.dataset.feedEvents?.split(' ').some(id => ids.includes(id)),
        );
      const target = restored
        ? restored.querySelector<HTMLElement>('button, a, summary')
        : preferenceFocus.current;
      target?.focus({ preventScroll: true });
    });
  };

  // LinkedIn düzeni: tek seçenek (gizle) menüye gömülmez, kartın sağ üstünde ✕; geri alma bildirimi aynı.
  const hideControl = (ids: string[]) => {
    if (!viewer?.id) return null;
    const label = t(`detail.feed.${ids.length > MAX_HIDDEN_FEED_EVENTS ? 'largeGroup' : 'hide'}`);
    return (
      <Button
        type="text"
        className="league-feed__options"
        icon={<CloseOutlined aria-hidden="true" />}
        aria-label={label}
        data-feed-focus={`options_${ids[0]}`}
        disabled={!preferences.ready || preferences.busy || ids.length > MAX_HIDDEN_FEED_EVENTS}
        title={
          !preferences.ready
            ? t(`detail.feed.${preferences.error || 'preferencesLoading'}`)
            : preferences.busy
            ? t('detail.feed.preferencesSaving')
            : label
        }
        onClick={() => changePreference(ids, true)}
      />
    );
  };

  const preferenceStatus = viewer?.id && (
    <div className="league-feed__preferences" ref={preferenceFocus} tabIndex={-1}>
      {!preferences.ready && !preferences.error && (
        <span role="status">{t('detail.feed.preferencesLoading')}</span>
      )}
      {preferences.error && (
        <p role="alert">
          {t(`detail.feed.${preferences.error}`)}{' '}
          <Button
            type="link"
            onClick={preferences.retry}
            disabled={preferences.busy}
            title={preferences.busy ? t('detail.feed.preferencesSaving') : undefined}
          >
            {t('detail.feed.retry')}
          </Button>
        </p>
      )}
      {preferences.undo.length > 0 && (
        <p role="status">
          {t('detail.feed.hiddenNotice')}{' '}
          <Button
            type="link"
            onClick={() => changePreference(preferences.undo, false)}
            loading={preferences.busy}
          >
            {t('detail.feed.undoHide')}
          </Button>
        </p>
      )}
      {preferences.hidden.size > 0 && (
        <Button
          type="link"
          onClick={() => changePreference([], false, true)}
          loading={preferences.busy}
        >
          {t('detail.feed.restoreHidden', { count: preferences.hidden.size })}
        </Button>
      )}
    </div>
  );

  // APG akış deseni: Page Down/Up kartlar arası, Ctrl+End/Home akıştan çıkış (12.3 #9).
  const onFeedKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    const feed = event.currentTarget;
    const articles = Array.from(feed.querySelectorAll<HTMLElement>(':scope > [role="article"]'));
    const current = articles.findIndex(article => article.contains(event.target as Node));
    const move = feedKeyMove(event.key, event.ctrlKey || event.metaKey, current, articles.length);
    if (!move) return;
    event.preventDefault();
    if (move.kind === 'exit') {
      focusOutside(feed, move.after)?.focus();
      return;
    }
    const target = articles[move.index];
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const renderItem = (item: LeagueFeed.Item, position?: { index: number; size: number }) => {
    const raw = feedItemEvents(item);
    const primary = item.kind === 'joins' ? item.events[0] : item.event;
    const details = item.kind === 'joins' ? item.events : item.kind === 'match' ? item.related : [];
    const entering = raw.map(event => fresh[event.id]).find(Boolean);
    return (
      <li
        style={entering ? { animationDelay: `${Math.min(entering.order, 6) * 40}ms` } : undefined}
        key={item.kind === 'event' ? item.event.id : item.id}
        role={position ? 'article' : undefined}
        tabIndex={position ? -1 : undefined}
        aria-posinset={position ? position.index + 1 : undefined}
        aria-setsize={position ? position.size : undefined}
        data-feed-id={item.kind === 'event' ? item.event.id : item.id}
        data-feed-events={raw.map(event => event.id).join(' ')}
        className={`league-feed__card league-feed__card--${
          item.kind === 'event' ? item.event.tur : item.kind
        }${
          (primary.tur === 'mac_sonucu' && primary.veri.mac_foto_var_mi) ||
          (primary.tur === 'gonderi' &&
            (primary.veri.foto_var_mi || primary.veri.baglanti?.tur === 'video'))
            ? ' league-feed__card--photo'
            : ''
        }${entering ? ` is-entering${entering.kind === 'new' ? ' is-new' : ''}` : ''}`}
      >
        <div className="league-feed__heading">
          {/* Kartta sıralama gerekçesi ("Sırana yakın" vb.) gösterilmez (5 Kas kararı). */}
          {feedOrder.pinned.has(primary.id) && (
            <span className="league-feed__tags">
              <span className="league-feed__pin">{t('detail.feed.lastNight')}</span>
            </span>
          )}
          {renderContext(primary)}
          {hideControl(raw.map(event => event.id))}
        </div>
        {item.kind === 'joins' ? renderJoins(item) : renderEvent(item.event)}
        {details.length > 0 && (
          // T3: maçın tek kilometre taşı (ilk galibiyet, 10. maç…) açık gelir; daha çoğu kapalı kalır.
          <details
            className="league-feed__group"
            open={item.kind === 'match' && details.length === 1 ? true : undefined}
          >
            <summary data-feed-focus={`details_${primary.id}`}>
              {t(`detail.feed.${item.kind === 'joins' ? 'joinDetails' : 'relatedMatchDetails'}`, {
                count: details.length,
              })}
            </summary>
            <ul>
              {details.map(event => (
                <li key={event.id}>
                  {hideControl([event.id])}
                  {renderContext(event)}
                  {renderEvent(event)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </li>
    );
  };

  // Akış boşken de yeni gelişme gelirse gösterilir.
  const incomingButton = (
    <div className="league-feed__incoming" aria-live="polite">
      {incoming && !refreshing && (
        <button type="button" className="league-feed__incoming-button" onClick={showIncoming}>
          <ArrowUpOutlined aria-hidden="true" />
          {t('detail.feed.incoming', { count: incoming.ids.length })}
        </button>
      )}
    </div>
  );

  if (firstPageError && !visibleEvents.length) {
    return (
      <div className="league-feed league-feed--empty">
        <p role="alert">
          <strong>{t('detail.feed.error')}</strong>
        </p>
        {loadError && <p>{t('detail.feed.loadError')}</p>}
        <Button
          onClick={loadMore}
          loading={loadingMore}
          disabled={refreshing}
          title={refreshing ? t('detail.feed.refreshing') : undefined}
        >
          {t('detail.feed.retry')}
        </Button>
        {footer}
      </div>
    );
  }

  if (!visibleEvents.length) {
    return (
      <div className="league-feed league-feed--empty" role="status" ref={root}>
        {incomingButton}
        {preferenceStatus}
        <UserAddOutlined aria-hidden="true" />
        {empty || (
          <>
            <p>
              <strong>{t('detail.feed.empty')}</strong>
            </p>
            <p>{t('detail.feed.emptyHint')}</p>
          </>
        )}
        {!footer && (
          <>
            <Button onClick={refresh} loading={refreshing}>
              {t('detail.feed.refresh')}
            </Button>
            {refreshError && <p role="alert">{t('detail.feed.refreshError')}</p>}
            <span role="status">{notice}</span>
          </>
        )}
        {footer}
      </div>
    );
  }

  return (
    <div
      className="league-feed"
      ref={root}
      onClickCapture={() => {
        const position = capturePosition();
        if (position) saveFeedPosition(scope, position);
      }}
    >
      {preview?.scope === scope && (
        <FeedPlayerPreview id={preview.id} onClose={() => setPreview(null)} />
      )}
      {!footer && (
        <div className="league-feed__pull" ref={pullIndicator} aria-hidden="true">
          <ArrowDownOutlined />
          <span>{t('detail.feed.pullToRefresh')}</span>
        </div>
      )}
      {!footer && (
        <div className="league-feed__refresh">
          <Button
            type={refreshError ? 'default' : 'text'}
            icon={refreshError ? undefined : <ReloadOutlined aria-hidden="true" />}
            onClick={refresh}
            loading={refreshing}
            disabled={loadingMore || pending.length > 0}
            aria-label={refreshError ? t('detail.feed.retryRefresh') : t('detail.feed.refresh')}
            title={
              loadingMore
                ? t('detail.feed.loadingPage')
                : pending.length
                ? t('detail.feed.reactionLoading')
                : undefined
            }
          >
            {refreshError ? (
              t('detail.feed.retryRefresh')
            ) : (
              <span className="visually-hidden">{t('detail.feed.refresh')}</span>
            )}
          </Button>
          <span role="status" className="visually-hidden">
            {notice}
          </span>
          {refreshError && <p role="alert">{t('detail.feed.refreshError')}</p>}
          {reactionError && (
            <p role="alert">
              {t('detail.feed.reactionError')}{' '}
              <Button type="link" onClick={() => setReactionRetry(value => value + 1)}>
                {t('detail.feed.retry')}
              </Button>
            </p>
          )}
        </div>
      )}
      <div className="league-feed__top">
        {incomingButton}
        {viewer?.id && (
          <FeedCongratsInbox
            key={`inbox-${scope}`}
            viewerId={viewer.id}
            events={visibleEvents.filter(event => !preferences.hidden.has(event.id))}
            reactions={knownReactions}
            ready
            showRows={task === 'inbox'}
            onCongrats={event => toggleCongrats(event)}
            onOpen={event => focusCard([event.id])}
          />
        )}
        {task === 'welcome' && (
          <FeedWelcome
            key={`welcome-${scope}`}
            events={newcomers}
            onWelcome={event => toggleCongrats(event)}
          />
        )}
        {task === 'story' && storyEvent && viewer?.id && (
          <FeedStoryReminder
            key={`story-${scope}`}
            event={storyEvent}
            viewerId={viewer.id}
            onOpen={event => focusCard([event.id])}
          />
        )}
        {task === 'catchup' && (
          <FeedMatchCatchUp
            key={`catchup-${scope}`}
            events={uncongratulated}
            onCongrats={event => toggleCongrats(event)}
            onOpen={event => focusCard([event.id])}
          />
        )}
      </div>
      <h2 className="visually-hidden">{t('detail.feed.title')}</h2>
      {preferenceStatus}
      {!sections.length && <p role="status">{t('detail.feed.hiddenEmpty')}</p>}
      {sharedOutside && (
        <section className="league-feed__shared" aria-labelledby="league-feed-shared">
          <h3 id="league-feed-shared">{t('detail.feed.shared')}</h3>
          <ul className="league-feed__list">
            {renderItem({ kind: 'event', event: sharedOutside })}
          </ul>
        </section>
      )}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- APG feed deseni: kartlar arası Page Down/Up */}
      <ul
        className="league-feed__list"
        role="feed"
        aria-label={t('detail.feed.title')}
        aria-busy={loadingMore || refreshing}
        onKeyDown={onFeedKeyDown}
      >
        {feedOrder.items.map((item, index) => (
          <React.Fragment key={item.kind === 'event' ? item.event.id : item.id}>
            {renderItem(item, { index, size: hasMore ? -1 : feedOrder.items.length })}
            {/* Fotoğraf şeridi ilk kartın altında (akış ilk ekranda başlasın, 3 Kasım). */}
            {index === 0 && stripItems.length >= 2 && (
              <li className="league-feed__strip-row" role="none">
                <FeedPhotoStrip items={stripItems} onOpen={id => focusCard([id])} />
              </li>
            )}
          </React.Fragment>
        ))}
        {loadingMore && (
          // Sonraki sayfa gelirken yer tutucu kart; durum metni düğmenin yükleniyor hâlinden gelir.
          <li className="league-feed__card league-feed__card--skeleton" aria-hidden="true">
            <Skeleton avatar active paragraph={{ rows: 2 }} />
          </li>
        )}
      </ul>
      {footer ? (
        <div className="league-feed__more">{footer}</div>
      ) : (
        <div className="league-feed__more" aria-live="polite">
          {loadError && (
            <p role="alert" className="league-feed__error">
              {t('detail.feed.loadError')}
            </p>
          )}
          {hasMore ? (
            <Button
              onClick={loadMore}
              loading={loadingMore}
              disabled={refreshing}
              title={refreshing ? t('detail.feed.refreshing') : undefined}
            >
              {loadError ? t('detail.feed.retry') : t('detail.feed.loadMore')}
            </Button>
          ) : (
            <p className="league-feed__end">{t('detail.feed.end')}</p>
          )}
        </div>
      )}
    </div>
  );
}

LeagueFeedList.defaultProps = {
  initialNextCursor: null,
  initialError: false,
  viewer: undefined,
  onSelectPlayer: undefined,
  footer: undefined,
  removedPhotoIds: [],
  onPhotoRemoved: undefined,
  fetchPage: undefined,
  initialContext: undefined,
  empty: undefined,
  photoStrip: false,
  sharedEvent: null,
};
